import { useCallback, useEffect, useRef, useState } from 'react';
import { noop } from '@utils/event';

/**
 * We can use this in 2 ways
 * 1. User sets execution to fire until call back is true
 * 2. Call iteration "maxIterations"
 * 3. Endless looping
 *
 * usage:
 const {finishedLoading} = useInterval(() => {
    if (('affirm' in window && window.affirm.jsReady())) {
      return true;
    }

    return false;
  }, 200, 20);

 result: Every 200ms it tests to see if callback is true, if not, increases iteration count.
 Will run every 200ms until true is returned or maxIterations is reached.

 If no delay and maxIterations are passed, runs loop. ie time counter.


 * @param callback
 * @param delay
 * @param maxIterations
 * @returns {{iterationCount: number, finished: boolean, callbackValue}}
 */
function useInterval(callback: BaseAnyFunction, delay: number | null, maxIterations?: number | null) {
  const savedCallback = useRef(noop);
  const iterationCount = useRef(0);
  const [finishedObject, setFinished] = useState({ finished: false, callbackValue: '' });

  const maxIterationsReached = useCallback(() => {
    if (maxIterations && iterationCount.current >= maxIterations) {
      return true;
    }

    return false;
  }, [maxIterations]);

  /* Remember the callback. */
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  /*
   - Set up the interval.
   - Communicate with caller to determine if predicate fn passed every tick
      i. if so, clear interval
   - If number ticks exceed max, clear interval.
   */
  useEffect(() => {
    function tick(id: ReturnType<typeof setInterval>) {
      const done = savedCallback.current();
      if (done || maxIterationsReached()) {
        setFinished({ finished: true, callbackValue: done });
        clearInterval(id);
      }

      iterationCount.current++;
    }

    if (delay !== null) {
      const id: ReturnType<typeof setInterval> = setInterval(() => tick(id), delay);
      return () => clearInterval(id);
    }
    return () => {};
  }, [delay, maxIterationsReached]);

  return { iterationCount, ...finishedObject };
}

export default useInterval;
